import { Card, Button, Text, Title } from '@bancobarigui/barihaus';
import { message } from 'antd';
import { PlusOutlined, DownloadOutlined, FileImageOutlined, FileTextOutlined } from '@ant-design/icons';
import { ElementGuard } from '@/components/PermissionGuard';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { S3Image } from '@/components/S3Image';
import { Attachment } from '@/services/types/inspection.types';

interface AttachmentsSectionProps {
  attachments: Attachment[];
  onAddAttachment: () => void;
  onDownloadAttachment: (attachment: Attachment) => void;
  downloadingFile: boolean;
}

export function AttachmentsSection({
  attachments,
  onAddAttachment,
  onDownloadAttachment,
  downloadingFile,
}: AttachmentsSectionProps) {
  const isImageFile = (fileName: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  return (
    <Card className="shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <Title level={4}>Anexos</Title>
        <ElementGuard permissions={[UserPermissions.inspections_address_edit]}>
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={onAddAttachment}
          >
            Adicionar Anexo
          </Button>
        </ElementGuard>
      </div>
      {attachments && attachments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {attachments.map((attachment, index) => {
            const isImage = isImageFile(attachment.fileName);
            const isS3File = attachment.filePath && attachment.filePath.startsWith('docman/');

            return (
              <Card key={index} className="!p-4 hover:shadow-md transition-shadow">
                {isImage && isS3File ? (
                  <div className="mb-3">
                    <S3Image
                      documentId={attachment.filePath}
                      alt={attachment.fileName}
                      className="w-full h-48 object-cover rounded"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-24 bg-gray-100 rounded mb-3">
                    {isImage ? (
                      <FileImageOutlined className="text-4xl text-gray-400" />
                    ) : (
                      <FileTextOutlined className="text-4xl text-gray-400" />
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Text strong className="block truncate" title={attachment.fileName}>
                    {attachment.fileName}
                  </Text>
                  <Text className="text-gray-500 text-sm block line-clamp-2">
                    {attachment.description}
                  </Text>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <Text className="text-gray-400 text-xs">
                      {attachment.fileSize > 0
                        ? `${(attachment.fileSize / 1024).toFixed(2)} KB`
                        : ''}
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadAttachment(attachment)}
                      loading={downloadingFile}
                    >
                      Baixar
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Text className="text-gray-500">Nenhum anexo cadastrado</Text>
      )}
    </Card>
  );
}
